import React, { useEffect, useMemo, useRef, useState } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { API_URL, getToken } from "@/lib/api";
import { tileCoords, tileSizeM } from "./siteGeo";

/**
 * Real-world ground for Tomt-modus. Two rings of web-mercator raster tiles (Mapbox satellite / Kartverket topo) are
 * laid out in rig metres around the site anchor: a high-detail ring under and around the rig, and a coarser context
 * ring (three zoom levels down) so the surroundings stay visible when the camera pulls back. Tiles are fetched
 * through the authenticated backend proxy, so vendor tokens never reach the browser. The whole group is rotated by
 * the site bearing so true north on the map matches the compass.
 */

const DETAIL_RADIUS = 3; // 7×7 detail tiles
const CONTEXT_RADIUS = 2; // 5×5 context tiles at zoom-3 (8× larger) → ~3 km across at z15
const CONTEXT_DROP = 3;
const Y_DETAIL = -0.02;
const Y_CONTEXT = -0.09;

const textureCache = new Map(); // url → Promise<THREE.Texture>
let maxAniso = 8;

function loadTexture(url) {
  if (textureCache.has(url)) return textureCache.get(url);
  const p = (async () => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken() || ""}` } });
    if (!res.ok) throw new Error(`tile ${res.status}`);
    const blob = await res.blob();
    let tex;
    if (typeof createImageBitmap === "function") {
      // decoded off the main thread, no object-URL lifecycle, no lazy re-decode when uploading to the GPU
      const bmp = await createImageBitmap(blob, { imageOrientation: "flipY", premultiplyAlpha: "none", colorSpaceConversion: "none" });
      tex = new THREE.Texture(bmp);
      tex.flipY = false;
    } else {
      const objectUrl = URL.createObjectURL(blob);
      try {
        tex = await new THREE.TextureLoader().loadAsync(objectUrl);
      } finally {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
      }
    }
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = maxAniso;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    tex.needsUpdate = true;
    return tex;
  })();
  p.catch(() => textureCache.delete(url));
  textureCache.set(url, p);
  return p;
}

function TilePlane({ url, size, dx, dz, y, onDone, placeholder }) {
  const [tex, setTex] = useState(null);
  const [failed, setFailed] = useState(false);
  const { invalidate } = useThree();
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    let alive = true;
    setTex(null);
    setFailed(false);
    loadTexture(url)
      .then((t) => { if (alive) { setTex(t); invalidate(); doneRef.current?.(url, true); } })
      .catch(() => { if (alive) { setFailed(true); invalidate(); doneRef.current?.(url, false); } });
    return () => { alive = false; };
  }, [url, invalidate]);
  return (
    <mesh position={[dx, y, dz]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
      <planeGeometry args={[size, size]} />
      {/* distinct keys: the textured material must be a fresh instance so its shader program is compiled with the map */}
      {tex ? <meshBasicMaterial key="tex" map={tex} toneMapped={false} /> : <meshBasicMaterial key="ph" color={failed ? placeholder.failed : placeholder.loading} toneMapped={false} />}
    </mesh>
  );
}

/** Tile grid around (lat, lng) at zoom z, in metres relative to the anchor. */
function layoutTiles(lat, lng, z, radius, layerId) {
  const { fx, fy, n } = tileCoords(lat, lng, z);
  const size = tileSizeM(lat, z);
  const tx0 = Math.floor(fx), ty0 = Math.floor(fy);
  const out = [];
  for (let j = -radius; j <= radius; j++) {
    for (let i = -radius; i <= radius; i++) {
      const tx = tx0 + i, ty = ty0 + j;
      if (ty < 0 || ty >= n) continue;
      const wx = ((tx % n) + n) % n;
      out.push({ key: `${z}/${wx}/${ty}`, url: `${API_URL}/site/tiles/${layerId}/${z}/${wx}/${ty}`, size, dx: (tx + 0.5 - fx) * size, dz: (ty + 0.5 - fy) * size });
    }
  }
  return out;
}

export function SiteGround({ site, layer, onStatus }) {
  const { gl } = useThree();
  useEffect(() => { maxAniso = Math.min(8, gl.capabilities.getMaxAnisotropy?.() || 8); }, [gl]);
  const lat = site?.lat, lng = site?.lng, zoom = site?.zoom;
  const layerId = layer?.id || "satellite";
  const maxZoom = layer?.max_zoom ?? 19;
  const minZoom = layer?.min_zoom ?? 3;

  const { detail, context } = useMemo(() => {
    if (lat == null || lng == null) return { detail: [], context: [] };
    const z = Math.max(minZoom, Math.min(maxZoom, Math.round(zoom || 18)));
    const zc = Math.max(minZoom, z - CONTEXT_DROP);
    return { detail: layoutTiles(lat, lng, z, DETAIL_RADIUS, layerId), context: zc < z ? layoutTiles(lat, lng, zc, CONTEXT_RADIUS, layerId) : [] };
  }, [lat, lng, zoom, layerId, maxZoom, minZoom]);

  // aggregate load progress for the status bar (detail ring only – that is what the user is waiting for)
  const progress = useRef({ loaded: 0, failed: 0, total: 0 });
  const detailUrls = useMemo(() => new Set(detail.map((t) => t.url)), [detail]);
  useEffect(() => {
    progress.current = { loaded: 0, failed: 0, total: detail.length };
    onStatus?.({ ...progress.current });
  }, [detail, onStatus]);
  const onDone = useMemo(() => (url, ok) => {
    if (!detailUrls.has(url)) return;
    const p = progress.current;
    if (ok) p.loaded += 1; else p.failed += 1;
    onStatus?.({ ...p });
  }, [detailUrls, onStatus]);

  // placeholders take the layer's character: dark earth for imagery, paper white for the topo map
  const placeholder = layerId === "topo" ? { loading: "#f2f4f6", failed: "#e9ecef" } : { loading: "#4b5a4e", failed: "#5c6a5e" };

  if (!site || lat == null || lng == null) return null;
  return (
    <group position={[site.anchor_x || 0, 0, site.anchor_z || 0]} rotation={[0, ((site.bearing_deg || 0) * Math.PI) / 180, 0]}>
      {context.map((t) => <TilePlane key={`c-${t.key}`} url={t.url} size={t.size + 0.05} dx={t.dx} dz={t.dz} y={Y_CONTEXT} placeholder={placeholder} />)}
      {detail.map((t) => <TilePlane key={`d-${t.key}`} url={t.url} size={t.size + 0.02} dx={t.dx} dz={t.dz} y={Y_DETAIL} onDone={onDone} placeholder={placeholder} />)}
      {/* anchor mark: the project coordinate itself */}
      <mesh position={[0, 0.015, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
        <ringGeometry args={[0.55, 0.75, 40]} />
        <meshBasicMaterial color="#266af3" transparent opacity={0.9} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[0, 0.015, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
        <circleGeometry args={[0.18, 24]} />
        <meshBasicMaterial color="#266af3" transparent opacity={0.9} depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  );
}
